import { sortBy } from "lodash"
import { FC, useState } from "react"
import { } from '../../../Services/Extensions/Boolean'
import { Button } from "../../buttons/button"
import { CheckBox } from "../../controls/checkbox"
import { BaseModalWindow } from "../../modalWindows/BaseModalWindow"
import styles from "./gridUiSettings.module.scss"
import GridWrapper from "../../controls/GridWrapper"
import { Select } from "../../selects/select"
import { GridRowHeightEnumOption } from "../../../@types/enumsGlobal"
import { useUserContext } from "../../../system/providers/userContextProvider"
import { useAppContext } from "../../../system/providers/appContextProvider"

interface IGridUiSettingsProps {
    plugin: IPluginSettings
    defaultPlugin: IPluginSettings
    save: (plugin: IPluginSettings) => void
    cancel: () => void
    hideRowHeightSetting?: boolean
}

const GridUISettings: FC<IGridUiSettingsProps> = (props) => {
    const appCtx = useAppContext();
    const userCtx = useUserContext();

    const [plugin, setPlugin] = useState(props.plugin)

    const changeDefaultPlugin = (): void => {
        if (userCtx.idGlobal) {
            appCtx.uiApiService.uiPluginSettingsRequest.getByMnemocode(props.plugin.mnemocode, userCtx.idGlobal, (response) => {
                if (response.respType == 'isCompleted') {

                    const serverData = response.data;

                    const normalizedPlugin = {
                        name: serverData.name,
                        mnemocode: serverData.mnemocode,
                        permission: props.defaultPlugin.permission, 
                        columns: serverData.gridColumns || [],
                        rowHeight: serverData.rowHeight
                    };

                    normalizedPlugin.columns.sort((a, b) => a.order - b.order);
                    setPlugin(JSON.parse(JSON.stringify(normalizedPlugin)));
                }
                else {
                    setPlugin(JSON.parse(JSON.stringify(props.defaultPlugin)))
                }
            })
        }
        else {
            setPlugin(JSON.parse(JSON.stringify(props.defaultPlugin)))
        }
    }

    // Новая функция обновления без сброса пользовательских настроек
    const updatePlugin = (): void => {
        const currentColumns = plugin.columns;
        const defaultColumns = props.defaultPlugin.columns;
        
        // Создаем Map для быстрого поиска текущих колонок по propertyName
        const currentColumnsMap = new Map(currentColumns.map(c => [c.propertyName, c]));
        
        // Синхронизируем колонки
        const updatedColumns: IGridColumn[] = defaultColumns.map((defaultCol, index) => {
            const existingCol = currentColumnsMap.get(defaultCol.propertyName);
            
            if (existingCol) {
                // Колонка существует - сохраняем пользовательские настройки, но обновляем метаданные из default
                return {
                    ...defaultCol, // Берем актуальные метаданные из default (displayName, propertyType и т.д.)
                    order: existingCol.order, // Сохраняем пользовательский порядок
                    visibility: existingCol.visibility, // Сохраняем видимость
                    width: existingCol.width, // Сохраняем ширину
                    favoriteFilter: existingCol.favoriteFilter, // Сохраняем фильтр
                    // Сохраняем другие пользовательские настройки если есть
                    sorting: existingCol.sorting !== undefined ? existingCol.sorting : defaultCol.sorting,
                    changeable: existingCol.changeable !== undefined ? existingCol.changeable : defaultCol.changeable,
                    nullable: existingCol.nullable !== undefined ? existingCol.nullable : defaultCol.nullable,
                };
            } else {
                // Новая колонка из default - добавляем с настройками по умолчанию
                return {
                    ...defaultCol,
                    order: index, // Используем порядок из default как начальный
                };
            }
        });

        // Пересчитываем order чтобы не было дубликатов и сохранить относительный порядок
        // Сначала сортируем по существующему order, новые колонки (с большим order) идут в конец
        const sortedColumns = updatedColumns.sort((a, b) => {
            // Если обе колонки были в текущих настройках - сравниваем их order
            const aWasExisting = currentColumnsMap.has(a.propertyName);
            const bWasExisting = currentColumnsMap.has(b.propertyName);
            
            if (aWasExisting && bWasExisting) {
                return a.order - b.order;
            }
            // Новые колонки идут после существующих
            if (aWasExisting && !bWasExisting) return -1;
            if (!aWasExisting && bWasExisting) return 1;
            
            // Обе новые - сортируем по порядку в default
            return defaultColumns.findIndex(c => c.propertyName === a.propertyName) - 
                   defaultColumns.findIndex(c => c.propertyName === b.propertyName);
        });

        // Переназначаем order последовательно от 0
        const finalColumns = sortedColumns.map((col, idx) => ({
            ...col,
            order: idx
        }));

        setPlugin({
            ...plugin,
            columns: finalColumns,
            // Также обновляем другие поля из default если нужно (rowHeight и т.д.)
            rowHeight: props.defaultPlugin.rowHeight ?? plugin.rowHeight,
        });
    }

    return (
        <>
            <BaseModalWindow
                header={"Настройка таблицы"}
                ok={{ onClick: () => props.save(plugin), title: "Сохранить" }}
                cancel={{ onClick: () => props.cancel(), title: "Отмена" }}
            >
                <GridWrapper cols={2}> 
                    <Button
                        onClick={() => {
                            changeDefaultPlugin()
                        }}
                        variant='outprimary'
                        className={styles.defaultSettings}
                    >
                        Применить настройки по умолчанию
                    </Button>
                    <Button
                        onClick={() => {
                            updatePlugin()
                        }}
                        variant='outprimary'
                        className={styles.defaultSettings}
                    >
                        Обновить структуру
                    </Button>
                    {!props.hideRowHeightSetting && (
                        <Select
                            defaultOption={GridRowHeightEnumOption[0]}
                            onSelect={(option) => setPlugin({...plugin, rowHeight: option.value})}
                            options={GridRowHeightEnumOption}
                            value={GridRowHeightEnumOption?.find(c => c.value === plugin.rowHeight) ?? GridRowHeightEnumOption[0].value}
                            className={styles.defaultSettings}
                            label="Размер высоты строк"
                        />
                    )}
                </GridWrapper>
               

                <table className={styles.gridUiSettings}
                >
                    <thead style={{ tableLayout: "fixed" }}>
                        <tr>
                            <th>Наименование</th>
                            <th>Видимость</th>
                            <th>Фильтр</th>
                            <th>Порядок</th>
                            <th>Ширина</th>
                        </tr>
                    </thead>
                    <tbody>

                        {
                            sortBy(plugin.columns, i => i.order).map((i, index) => {
                                return (
                                    <tr key={index} className={styles.row}>
                                        <td draggable={true} data-order-number={i.order}
                                            onDragOver={(e) => {
                                                e.preventDefault()
                                                return false
                                            }}
                                            onDragStart={(e) => {
                                                e.dataTransfer.dropEffect = "move"
                                                e.dataTransfer.setData("application/json", JSON.stringify(i))
                                            }}
                                            onDrop={(e) => {
                                                let targetOrder = Number((e.target as HTMLElement).getAttribute("data-order-number"));
                                                let data = JSON.parse(e.dataTransfer.getData("application/json")) as IGridColumn
                                                let reorderedColumns = plugin.columns.map(c => {
                                                    if (c.order === data.order) {
                                                        c.order = targetOrder
                                                    }
                                                    else if (c.order === targetOrder) {
                                                        c.order = data.order
                                                    }
                                                    return c
                                                })
                                                let newState = { ...plugin }
                                                newState.columns = reorderedColumns;
                                                setPlugin(newState)
                                            }}
                                        >
                                            {i.displayName}
                                        </td>
                                        <td>
                                            <CheckBox 
                                                className={styles.checkBox} 
                                                id={i.propertyName + 'visibility'} 
                                                defaultChecked={i.visibility}
                                                disabled={(plugin.columns.filter((element) => element.visibility).length === 1 && plugin.columns.filter((element) => element.visibility)[0]?.order === index) ? true : false}
                                                onChanged={() => {
                                                    const newColumns = [...plugin.columns];
                                                    const index = newColumns.findIndex(si => si.propertyName === i.propertyName);
                                                    const prop = { ...newColumns[index] };
                                                    prop.visibility = !i.visibility;
                                                    newColumns[index] = prop;
                                                    setPlugin({ ...plugin, columns: newColumns });
                                                }} />
                                        </td>
                                        <td>
                                            <CheckBox 
                                                className={styles.checkBox} 
                                                id={i.propertyName + 'favoriteFilter'} 
                                                defaultChecked={i.favoriteFilter}
                                                onChanged={() => {
                                                    const newColumns = [...plugin.columns];
                                                    const index = newColumns.findIndex(si => si.propertyName === i.propertyName);
                                                    const prop = { ...newColumns[index] };
                                                    prop.visibility = !i.visibility;
                                                    newColumns[index] = prop;
                                                    setPlugin({ ...plugin, columns: newColumns });
                                                }} />
                                        </td>
                                        <td className={styles.noMouseEvent}>{i.order}</td>
                                        <td className={styles.noMouseEvent}>{i.width}</td>
                                    </tr>
                                )
                            })
                        }
                    </tbody>
                </table>
            </BaseModalWindow>
        </>
    )
}

export default GridUISettings